import { inject, Injectable } from '@angular/core';
import { delay, EMPTY, filter, lastValueFrom, map, Observable, shareReplay, Subject, switchMap, take, tap } from 'rxjs';
import { SignalRBase } from 'app/classes/signalR-base';
import { WINDOW } from 'app/core/core.module';
import { version } from "environments/sswv";

@Injectable({
  providedIn: 'root'
})
export class SignalRSWService extends SignalRBase {
  private sharedWorker!: SharedWorker;
  private readonly invokeEvents$ = new Subject<any>();
  private readonly onEvents$ = new Subject<{ methodName: string, args: Array<any> }>();
  private readonly window = inject(WINDOW);

  constructor() {
    super();
    this.initializeSharedWorker().pipe(
      switchMap(() => this.auth0Service.getAccessTokenSilently(/*{ cacheMode: 'cache-only' }*/)),
      take(1)
    ).subscribe({
      next: (token) => {
        const data = {
          orgId: this.organizationService.organizationSelectedId,
          token,
        };
        this.sharedWorker.port.postMessage({ command: 'connect', data });
      },
      error: (err) => {
        console.error(err);
        this.auth0Service.logout();
      }
    });

    this.reconnect$.subscribe(() => this.sharedWorker.port.postMessage({ command: 'reconnect', data: {} }));

    this.window.addEventListener('beforeunload', () => {
      this.sharedWorker.port.postMessage({ command: 'CLOSE_TAB', data: {} });
    });

    this.userOrganizationChanged$.pipe(
      filter((organizationId) => organizationId !== this.organizationService.organizationSelectedId),
      tap(_ => this.snackBar.open('The selected organization has been changed...')),
      delay(2000)
    ).subscribe((organizationId) => this.organizationService.select(organizationId));
  }

  readonly userOrganizationChanged$ = this.onV2('UserOrganizationChanged').pipe(
    map(args => {
      const { organizationId } = args;
      return organizationId
    })
  );

  readonly organizationCreated$ = this.on('OrganizationCreated').pipe(
    map(args => args[0] as string)
  );

  readonly liveClientsStarted$ = this.onV2('LiveClientStarted').pipe(
    map(args => {
      const { clientId, startedAt, sessionId, objectId, objectKind } = args;
      return { clientId, online: true, startedAt, totalSeconds: 0, sessionId, objectId, objectKind, closedAt: null };
    }),
    // shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly liveClientsStopped$ = this.onV2('LiveClientStopped').pipe(
    map(args => {
      const { clientId, closedAt, totalSeconds, sessionId } = args;
      return { clientId, closedAt, totalSeconds, online: false, sessionId, startedAt: null, objectId: null, objectKind: null };
    }),
    // shareReplay({ bufferSize: 1, refCount: true })
  );

  private initializeSharedWorker() {
    if (typeof SharedWorker === 'undefined') {
      console.error('SharedWorker is not supported in this environment');
      return EMPTY;
    }

    this.sharedWorker = new SharedWorker(new URL('/src/app/shared.worker', import.meta.url), { name: `signalR_${version}`, type: 'module' }); // this is the path of our shared-worker.js file.
    this.sharedWorker.port.start();
    this.sharedWorker.port.onmessage = (event) => {
      const { command, error, methodName, args, data, eventId } = event.data;
      this.zone.run(() => {
        switch (command) {
          case 'getToken':
            this.appMonitoringService.logException(new Error('Auth0: Status code 401'));
            lastValueFrom(this.auth0Service.getAccessTokenSilently().pipe(take(1)))
              .then((token) => this.sharedWorker.port.postMessage({ command: 'setToken', data: { token } }))
              .catch(err => {
                console.error(err);
                this.appMonitoringService.logException(err);
                this.auth0Service.logout();
              });
            break;
          case 'connected':
            this._isConnect$.next(true);
            break;
          case 'connectionError':
            this.connectionErrorHandler(error);
            break;
          case 'reconnecting':
            this.reconnectingHandler(error);
            break;
          case 'invoke':
            this.invokeEvents$.next({ eventId, data });
            break;
          case 'invokeV2':
            this.invokeEvents$.next({ eventId, data });
            break;
          case 'on':
            this.onEvents$.next({ methodName, args });
            break;
          case 'onV2':
            this.onEvents$.next({ methodName, args });
            break;
          case 'invokeError':
            this.invokeEvents$.next({ eventId, error });
        }
      });
    };
    return this.auth0Service.isAuthenticated$.pipe(
      filter(isAuthenticated => isAuthenticated)
    );
  }

  on(methodName: string): Observable<Array<any>> {
    const data = {
      methodName,
    };
    return this.isConnected$.pipe(
      filter(isConnect => isConnect),
      tap(() => this.sharedWorker.port.postMessage({ command: 'on', data })),
      switchMap(() => this.onEvents$.asObservable()),
      filter((event) => event.methodName === methodName),
      map((event) => event.args),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  onV2(methodName: string): Observable<any> {
    const data = {
      methodName,
    };
    return this.isConnected$.pipe(
      filter(isConnect => isConnect),
      tap(() => this.sharedWorker.port.postMessage({ command: 'onV2', data })),
      switchMap(() => this.onEvents$.asObservable()),
      filter((event) => event.methodName === methodName),
      map((event) => event.args),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  invoke(methodName: string, args: any[]) {
    return new Promise((resolve, reject) => {
      const eventId = Date.now();
      const data = {
        eventId,
        methodName,
        args
      };
      this.sharedWorker.port.postMessage({ command: 'invoke', data });
      this.invokeEvents$.asObservable().pipe(
        filter(data => data.eventId === eventId),
        take(1)
      ).subscribe((invokeData) => {
        const { error, data } = invokeData;
        console.error(error);
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }

  invokeV2(methodName: string, args: any) {
    return new Promise<any>((resolve, reject) => {
      const eventId = Date.now();
      const data = {
        eventId,
        methodName,
        args
      };
      this.sharedWorker.port.postMessage({ command: 'invokeV2', data });
      this.invokeEvents$.asObservable().pipe(
        filter(data => data.eventId === eventId),
        take(1)
      ).subscribe((invokeData) => {
        const { error, data } = invokeData;
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }
}